import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { I18nUtil } from '../../utils/i18n/I18nUtil';
import {
  CookieDisclaimerControllableComponent,
} from './controller/CookieDisclaimerControllableComponent.interface';
import { CookieDisclaimerController } from './controller/CookieDisclaimerController';
import { COOKIE_DISCLAIMER_DEFAULTS as DEFAULTS } from './controller/CookieDisclaimerDefaults';

@Component({
  tag: 'bahf-cookie-disclaimer-dpl3',
  styleUrl: 'bahf-cookie-disclaimer.scss',
  assetsDirs: ['i18n-dpl3'],
  shadow: true,
  scoped: false,
})
export class BahfCookieDisclaimer implements CookieDisclaimerControllableComponent {
  controller = new CookieDisclaimerController(this);

  @Element() el: HTMLElement;
  @Prop({ mutable: true }) forceVisible: boolean = DEFAULTS.forceVisible;

  // Cookie checkbox Zustände
  @State() statisticCookies = DEFAULTS.statisticCookies;
  @State() personalCookies = DEFAULTS.personalCookies;
  @State() marketingCookies = DEFAULTS.marketingCookies;

  // Sichtbarkeit des Modals (falls visible = auto)
  @State() autoVisible = DEFAULTS.autoVisible;

  /**
   * Dieses Event wird getriggert, wenn der Benutzer auf den "Alle Cookies akzeptieren" Button im Cookie-Disclaimer klickt.<br/>
   * <br/>
   * Die Payload enthält kein Objekt, da alle Cookie-Typen akzeptiert werden.<br/>
   * Dieses Event zeigt an, dass der Benutzer zugestimmt hat, alle Arten von Cookies zu akzeptieren,
   * einschließlich Statistik-, Personalisierungs- und Marketing-Cookies.
   * Event-Listener können dieses Event nutzen, um Cookie-Einstellungen zu aktualisieren oder
   * andere notwendige Aktionen durchzuführen, wenn alle Cookies akzeptiert wurden.
   */
  @Event({ eventName: 'bahfCdAllAccepted' }) bahfCdAcceptAll: EventEmitter;


  /**
   * Dieses Event wird getriggert, wenn der Benutzer auf den "Auswahl bestätigen" Button im Cookie-Disclaimer klickt.<br/>
   * <br/>
   * Die Payload enthält ein Objekt mit drei Feldern:<br/>
   * <br/>
   * - cookieConsentChecked: ein Boolean-Wert (true/false).
   * Gibt an, ob die notwendigen Cookies akzeptiert wurden. Dieser Wert ist immer true, da notwendige Cookies
   * nicht abgelehnt werden können.<br/>
   * <br/>
   * - personalizationConsentChecked: ein Boolean-Wert (true/false).
   * Zeigt an, ob der Benutzer der Verwendung von Personalisierungs-Cookies zugestimmt hat.<br/>
   * <br/>
   * - marketingConsentChecked: ein Boolean-Wert (true/false).
   * Zeigt an, ob der Benutzer der Verwendung von Marketing-Cookies zugestimmt hat.<br/>
   * <br/>
   * Diese Informationen können von Event-Listenern genutzt werden, um die entsprechenden Cookie-Einstellungen
   * zu aktualisieren oder andere erforderliche Aktionen basierend auf der Benutzerauswahl durchzuführen.
   */
    @Event({ eventName: 'bahfCdSelectionAccepted' }) bahfCdAcceptSelection: EventEmitter<{
      cookieConsentChecked: boolean;
      personalizationConsentChecked: boolean;
      marketingConsentChecked: boolean;
    }>;

  // Initialies setzen der cookie checkbox Zustände
  // STENCIL LIFECYCLE: Ausführung einmalig beim laden der Komponente
  componentWillLoad() {
    this.controller.loadConsentStatesFromCookies();
  }

  componentDidLoad(): void {
    const modal = this.el.shadowRoot.querySelector('bahf-cd-modal');
    modal.classList.remove('initializing');
  }

  render() {
    const isOpen = this.forceVisible || this.autoVisible;
    return (
      <Host aria-hidden={isOpen ? 'false' : 'true'}>
        <bahf-cd-modal
          isOpen={isOpen}
          controller={this.controller}
          class="modal-open initializing">
          <div class="modal-header">
            {/* Cookie-Disclaimer-Überschrift */}
            <h1 class="modal-title" data-testid="bahf-cookie-disclaimer-h1">
              <button
                type="button"
                class="ba-modal-close"
                aria-label={I18nUtil.getTranslation('ModalCloseAria')}
                title={I18nUtil.getTranslation('ModalCloseTitle')}
                onClick={this.controller.handleClose}
                data-testid="bahf-cookie-disclaimer-btn-schliessen"
              ></button>
              {I18nUtil.getTranslation("ModalUeberschrift")}
            </h1>
          </div>
          <div class="modal-body">

            <div class="ba-copytext" innerHTML={I18nUtil.getTranslation("Text")} />

            <form class="ba-progress-form-area">
              <fieldset id="fieldset-checkbox-group1-id" class="ba-input-group">
                <legend>
                  {I18nUtil.getTranslation("AuswahlText")}
                </legend>
                <div class="row ba-form-row">
                  <div class="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <bahf-cd-checkbox
                      label={I18nUtil.getTranslation("StatistikUeberschrift")}
                      inputId="ba-hf-cd-stat-cookies-cb"
                      onToggle={() => this.controller.setStatisticCookies(!this.statisticCookies)}
                      checked={this.statisticCookies}
                    />
                  </div>
                  <div class="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <bahf-cd-checkbox
                      label={I18nUtil.getTranslation("PersonalisierungUeberschrift")}
                      inputId="ba-hf-cd-pers-cookies-cb"
                      onToggle={() => this.controller.setPersonalCookies(!this.personalCookies)}
                      checked={this.personalCookies}
                    />
                  </div>
                  <div class="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <bahf-cd-checkbox
                      label={I18nUtil.getTranslation("MarketingUeberschrift")}
                      inputId="ba-hf-cd-mark-cookies-cb"
                      onToggle={() => this.controller.setMarketingCookies(!this.marketingCookies)}
                      checked={this.marketingCookies}
                    />
                  </div>
                </div>
              </fieldset>
            </form>
            <bahf-cd-collapse>
              <div class="ba-copytext">
                <h2 class="h6">
                  {I18nUtil.getTranslation("NotwendigeUeberschrift")}
                </h2>
                <p>
                  {I18nUtil.getTranslation("NotwendigeText")}
                </p>
                <h2 class="h6">
                  {I18nUtil.getTranslation("StatistikUeberschrift")}
                </h2>
                <p>
                  {I18nUtil.getTranslation("StatistikText")}
                </p>
                <h2 class="h6">
                  {I18nUtil.getTranslation("PersonalisierungUeberschrift")}
                </h2>
                <p>
                  {I18nUtil.getTranslation("PersonalisierungText")}
                </p>
                <h2 class="h6">
                  {I18nUtil.getTranslation("MarketingUeberschrift")}
                </h2>
                <p>
                  {I18nUtil.getTranslation("MarketingText")}
                </p>
              </div>
            </bahf-cd-collapse>
          </div>
          <div class="modal-footer">
            <button
              class="ba-btn ba-btn-contrast"
              aria-label={I18nUtil.getTranslation('AblehnenBtnAriaLabel')}
              onClick={this.controller.handleNoSelection}
              data-testid="bahf-cookie-disclaimer-btn-ablehnen"
            >
              {I18nUtil.getTranslation("AblehnenBtnText")}
            </button>
            <button
              class="ba-btn ba-btn-contrast"
              aria-label={I18nUtil.getTranslation('AuswahlBtnAriaLabel')}
              onClick={this.controller.handleAcceptSelection}
              data-testid="bahf-cookie-disclaimer-btn-auswahl"
            >
              {I18nUtil.getTranslation("AuswahlBtnText")}
            </button>
            <button
              class="ba-btn ba-btn-contrast"
              aria-label={I18nUtil.getTranslation('AlleBtnAriaLabel')}
              onClick={this.controller.handleAcceptAll}
              data-testid="bahf-cookie-disclaimer-btn-alle"
            >
              {I18nUtil.getTranslation("AlleBtnText")}
            </button>
          </div>
        </bahf-cd-modal>
      </Host>
    );
  }
}
